import { format } from "date-fns";

import { IDate } from "shared/types";
import { getRegionalSettingsOptions } from "shared/utils";
import { DateFormat, TimeFormat } from "./getRegionalSettingsOptions";

interface IDateFormat {
  pattern: DateFormat;
  datePattern: string[];
}

export const dmyDateFormat: IDateFormat = {
  pattern: DateFormat.dmy,
  datePattern: ["d", "m", "Y"],
};

export const mdyDateFormat: IDateFormat = {
  pattern: DateFormat.mdy,
  datePattern: ["m", "d", "Y"],
};

export const ymdDateFormat: IDateFormat = {
  pattern: DateFormat.ymd,
  datePattern: ["Y", "m", "d"],
};

export const getDateFormat = (dateFormat?: DateFormat): IDateFormat => {
  if (dateFormat) {
    switch (dateFormat) {
      case DateFormat.mdy: {
        return mdyDateFormat;
      }
      case DateFormat.ymd: {
        return ymdDateFormat;
      }
      default: {
        return dmyDateFormat;
      }
    }
  }

  return dmyDateFormat;
};

// Format unix timestamp as human-readable date and time string.
export const formatDateTime = (
  str: string,
  config?: {
    timeFormat?: TimeFormat;
    dateFormat?: DateFormat;
    dateOnly?: boolean;
    longForm?: boolean;
  }
): string | null => {
  if (str === undefined || str === null || str.trim() === "") {
    return null;
  }

  let date = str;

  if (config?.dateOnly) {
    const parts = str.split("T");
    if (parts.length) {
      date = parts[0];
    }
  }

  const dateString = new Date(date);
  const dateTime = new Date(
    dateString.valueOf() + dateString.getTimezoneOffset() * 60 * 1000
  );

  let pattern = "";

  const timePattern = config?.timeFormat === TimeFormat.twelve ? "p" : "HH:mm";

  const { pattern: defaultDateFormat } = getDateFormat(config?.dateFormat);
  let datePattern = defaultDateFormat.replace("mm", "MM");

  if (config?.longForm) {
    datePattern = "PPPP";
  }

  if (config?.dateOnly) {
    pattern = datePattern;
  } else {
    pattern = `${datePattern} 'at' ${timePattern}`;
  }

  return format(dateTime, pattern);
};

// 2021-12-31 to 31/12/2021 or 12/31/2021 based on locale
export const isoToDateString = (iso: string, dateFormat?: DateFormat) => {
  const { pattern } = getDateFormat(dateFormat);

  return format(new Date(iso), pattern.replace("mm", "MM"));
};

export const createNewDate = (date?: string) => {
  let dateString;

  if (!date) {
    dateString = new Date();
  } else {
    dateString = new Date(date);
  }

  const dateTime = new Date(
    dateString.valueOf() + dateString.getTimezoneOffset() * 60 * 1000
  );

  return dateTime;
};

// 31/12/2021 or 12/31/2021 to 2021-12-31
export const dateStringToISO = (
  date: string,
  dateFormat?: DateFormat,
  separator?: string
) => {
  const parts = date.split(separator || "/");
  const { datePattern } = getDateFormat(dateFormat);

  let day = parts[0];
  let month = parts[1];
  const year = parts[2];

  if (datePattern[0] === "m") {
    month = parts[0];
    day = parts[1];
  }

  return `${year}-${month}-${day}`;
};

export enum AM_PM {
  am = "am",
  pm = "pm",
}

export const getTwentyFourHourTime = ({
  time,
  amPm,
}: {
  time: string;
  amPm: AM_PM | null;
}) => {
  if (time && time.length > 4) {
    const [h, m, s = "00"] = time.split(":");
    let twentyFourHour = h;

    const intHour = parseInt(h);

    if (amPm === AM_PM.am) {
      twentyFourHour = intHour === 12 ? "00" : h;
    }

    if (amPm === AM_PM.pm) {
      twentyFourHour = intHour === 12 ? h : `${intHour + 12}`;
    }

    return `${twentyFourHour}:${m}:${s}`;
  }
  return null;
};

export const getTwelveHourTime = (
  time: string,
  format?: "hh:mm" | "hh:mm:ss"
) => {
  if (time.indexOf("_") !== -1) return { time, amPm: AM_PM.am };
  if (time === "undefined") return { time: "12:00:00", amPm: AM_PM.am };

  const [h, m, s = "00"] = time.split(":");

  if (h === "null" || m === "null") return { time: "", amPm: AM_PM.am };

  const intHour = parseInt(h);
  let amPm = intHour < 12 || intHour === 24 ? AM_PM.am : AM_PM.pm;

  const twelveHour = intHour % 12 || 12;
  const hour = twelveHour > 9 ? twelveHour : `0${twelveHour}`;

  if (format === "hh:mm") {
    return { time: `${hour}:${m}`, amPm };
  }

  return { time: `${hour}:${m}:${s}`, amPm };
};

// 2021-12-31 to 2021-12-31T00:00+00:00
export const isoToUTCString = ({
  iso,
  type = "start",
  hour,
  timeZone,
}: {
  iso: string;
  type?: "start" | "end";
  hour?: string;
  timeZone?: string;
}) => {
  const { timezoneToUtc } = getRegionalSettingsOptions();
  const defaultHour = type === "start" ? "00:00:00" : "23:59:59";
  const timeZoneString = timeZone ? timezoneToUtc(timeZone) : "+00:00";

  return `${format(createNewDate(iso), "yyyy-MM-dd")}T${
    hour || defaultHour
  }${timeZoneString}`;
};

export const isoToUTCStringEvent = ({
  iso,
  type = "start",
  hour,
  timeZone,
}: {
  iso: string;
  type?: "start" | "end";
  hour?: string;
  timeZone?: string;
}) => {
  const { timezoneToUtc } = getRegionalSettingsOptions();
  const defaultHour = type === "start" ? "00:00:00" : "23:59:59";
  const timeZoneString = timeZone ? timezoneToUtc(timeZone) : "+00:00";

  // https://stackoverflow.com/questions/48172772/time-zone-issue-involving-date-fns-format#:~:text=00%3A00%20PM%27-,Solution,-If%20you%20know
  // We had a problem here where the date was subtracted by the timezone offset
  // This is a workaround to fix that
  const date = new Date(iso);
  const dtDateOnly = new Date(
    date.valueOf() + date.getTimezoneOffset() * 60 * 1000
  );

  return `${format(dtDateOnly, "yyyy-MM-dd")}T${
    hour || defaultHour
  }${timeZoneString}`;
};

export const addDays = (date: string, number: number) => {
  const newDate = new Date(date);
  return new Date(newDate.setDate(newDate.getDate() + number));
};

export const isoToDate = (iso: string) => {
  const parts = iso.slice(0, 10).split("-");

  const date = {
    day: +parts[2],
    month: +parts[1],
    year: +parts[0],
    shortWeekday: format(new Date(iso), "EEE"),
    weekday: format(new Date(iso), "EEEE"),
  } as IDate;

  return date;
};

export const dateToIso = (date: IDate) => {
  const dateString = new Date(Date.UTC(date.year, date.month, date.day));
  const dateTime = new Date(
    dateString.valueOf() + dateString.getTimezoneOffset() * 60 * 1000
  );

  return format(dateTime, "yyyy-MM-dd");
};

export const isValidDate = (date: string) => {
  try {
    const d = new Date(date);
    return !isNaN(d.getTime());
  } catch {
    return false;
  }
};

// 2021-12-31T00:00+01:00 to
// {
//  date: "2021-12-31",
//  time: "00:00",
// }
export const utcStringToDateTimeTimezone = (utc: string) => {
  const date = utc.split("T")?.[0];
  const timeTimezone = utc.split("T")?.[1];
  const time = timeTimezone.substring(0, 5);

  return {
    date,
    time,
  };
};

export const formatTime = ({
  time,
  timeFormat,
}: {
  time: string;
  timeFormat: TimeFormat;
}) => {
  const twelveTime = getTwelveHourTime(time, "hh:mm");

  return timeFormat === TimeFormat.twelve
    ? `${twelveTime.time} ${twelveTime.amPm}`
    : time.substring(0, 5);
};

export const isUTCDateTime = (utc: string) => utc.includes("T");
